import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import { 
  Phone, 
  ArrowLeft, 
  Clock, 
  Calendar,
  ArrowRight,
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  CheckCircle2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { blogPosts } from './BlogPage';

// Full blog content
const blogContent = {
  'industry-trained-ai-knowledge': {
    content: `
## Why Generic Chatbots Fail Contractors

You've probably encountered AI chatbots before — the kind that can barely answer basic questions without getting confused. So why would an AI receptionist be any different for your contracting business?

**The answer is simple: industry-specific training.**

Our AI isn't a generic question-answering machine. It's been pre-loaded with deep knowledge of your industry, your region, and the specific challenges your customers face.

### The Knowledge Base Difference

Most AI systems start from scratch. They know nothing about plumbing codes, HVAC troubleshooting, or electrical safety requirements. When a customer asks about "water heater maintenance in Alaska," a generic AI has no idea how to respond.

**Our system is different.** We've built an industry + regional knowledge base that includes:

- **Plumbing:** Frozen pipe prevention, water heater maintenance, septic system care, local code requirements
- **HVAC:** Furnace troubleshooting, emergency protocols, seasonal maintenance schedules
- **Electrical:** Safety requirements, permit information, common issues by region
- **Roofing:** Storm damage assessment, material recommendations, cold-weather considerations
- **And more...**

### Regional Intelligence Matters

Here's something most AI companies miss: **regional context matters enormously** in contracting.

A plumber in Alaska deals with frozen pipes for 6+ months of the year. A plumber in Florida rarely thinks about frozen pipes but handles hurricane prep and humidity-related issues constantly.

Our knowledge base is organized by region:
- **Alaska:** Extreme cold protocols, frozen pipe prevention, heating system priorities
- **Florida:** Hurricane prep, humidity management, tropical climate considerations
- **Pacific Northwest:** Rain and moisture issues, mold prevention
- **Southwest:** Heat-related equipment stress, water conservation
- **And every other US region...**

When a customer calls your Alaska HVAC business at 2 AM about a furnace failure, our AI knows exactly what questions to ask and what emergency steps to recommend — because it understands Alaska winters.

### Premium Contractors Contribute

Here's what makes our system truly unique: **the knowledge base grows with real contractor insights.**

Premium subscribers can add their own FAQs, troubleshooting tips, and best practices to the system. When you discover a common customer question or develop a better way to explain a service, you can contribute that knowledge.

This creates a virtuous cycle:
1. Contractors share what works
2. The AI gets smarter
3. Everyone's customers get better service
4. The whole network benefits

### What This Means for Your Customers

When someone calls your business, they get:

- **Instant, accurate answers** about your services
- **Industry-specific guidance** (not generic responses)
- **Regional awareness** (the AI knows your local challenges)
- **Expert-level knowledge** (drawn from hundreds of contractor insights)

All delivered in under 60 seconds, 24/7.

### The Bottom Line

Generic AI chatbots frustrate customers because they lack context. Our industry-trained system understands your business, your region, and your customers' needs.

That's why our contractors see 97% customer satisfaction rates — the AI doesn't just take messages, it actually helps.

**Ready to see the difference?** Book a demo and ask our AI about your specific industry. You'll see exactly how much smarter industry training makes it.
    `,
    cta: true
  },
  'why-contractors-lose-leads': {
    content: `
## The Hidden Cost of Missed Calls

Every contractor knows the feeling. You're elbow-deep in a repair job, your phone rings, and by the time you can answer, the customer has already moved on. Whether it's a burst pipe in Texas or a furnace failure in Minnesota, emergency calls don't wait.

**Here are the 5 biggest reasons contractors lose money to missed calls:**

### 1. No After-Hours Coverage

60% of emergency calls come outside regular business hours. When a furnace dies at 2 AM or a pipe bursts on Sunday morning, customers don't leave voicemails—they call the next company on their list.

**The fix:** AI automation responds instantly, 24/7, qualifying emergencies and capturing lead information even while you sleep.

### 2. Slow Response Times

Studies show that customers contact an average of 3 contractors when they have a problem. **The first to respond wins 78% of the time.**

Most contractors take 4+ hours to return calls. By then, the job is gone.

**The fix:** AI responds in under 60 seconds, every time, keeping you first in line.

### 3. The Voicemail Black Hole

Here's a shocking stat: **78% of callers won't leave a voicemail.** They assume you're too busy or don't care, and they move on.

**The fix:** AI engages callers in a natural conversation, gathering their information and scheduling appointments without ever sending them to voicemail.

### 4. Manual Appointment Booking

Even when you do connect with a lead, manually checking your calendar and scheduling appointments wastes valuable time. It also leads to double-bookings and missed follow-ups.

**The fix:** AI integrates with your calendar to book appointments in real-time, sending confirmations to both you and the customer.

### 5. Lost Lead Information

How many times have you scribbled a phone number on a scrap of paper, only to lose it later? Without a system, valuable customer data disappears.

**The fix:** Every interaction is logged automatically, creating a database of leads you can follow up with.

## The Bottom Line

Each missed call represents $800-$2,000 in lost revenue (or more for emergency services). Over a week, that adds up to serious money.

AI automation isn't about replacing human connection—it's about making sure no lead slips through the cracks while you're busy doing what you do best: serving your customers.

**Ready to stop losing leads?** Book a free 10-minute demo to see AI lead response in action.
    `,
    cta: true
  },
  'ai-vs-answering-service': {
    content: `
## The Great Debate: AI vs Human Answering Services

When it comes to handling calls after hours, contractors typically have three options:
1. Miss the calls (and lose the business)
2. Hire a traditional answering service
3. Use AI automation

Let's break down how these options compare.

### Cost Comparison

| Option | Monthly Cost | Setup Fee |
|--------|-------------|-----------|
| Traditional Answering Service | $300-800/mo | $100-200 |
| Full-time Receptionist | $3,500-4,500/mo | Training time |
| AI Automation | $397/mo | $1,997 one-time |

**Winner: AI** - Lower ongoing costs with better capabilities

### Response Time

- **Answering Service:** 15-30 seconds (if staffed)
- **AI Automation:** Under 60 seconds, guaranteed

**Winner: AI** - Instant response, every time

### Appointment Booking

- **Answering Service:** Takes messages, you call back to book
- **AI Automation:** Books directly into your calendar

**Winner: AI** - No phone tag required

### After-Hours Availability

- **Answering Service:** Depends on staffing (holidays can be spotty)
- **AI Automation:** 24/7/365, no exceptions

**Winner: AI** - Never takes a day off

### Industry Knowledge

- **Answering Service:** Generic scripts, limited training
- **AI Automation:** Trained on your specific industry (HVAC, plumbing, electrical, etc.)

**Winner: AI** - Knows your business

### Customer Experience

This is where it gets interesting. Modern AI sounds remarkably natural. Most customers can't tell the difference—and frankly, they don't care as long as their problem gets solved.

What customers DO care about:
- Getting a quick response ✓
- Not being put on hold ✓
- Having their appointment booked ✓

**Winner: Tie** - Both can provide good experience, but AI is more consistent

## The Verdict

For contractors who need reliable after-hours coverage, AI automation provides better value at lower cost. The one-time setup investment pays for itself within the first few months through captured leads.

Traditional answering services still have their place—particularly for businesses that need a human touch for complex sales calls. But for lead capture and appointment booking, AI is the clear winner.
    `,
    cta: true
  },
  'winter-emergency-response': {
    content: `
## Extreme Weather: The Ultimate Test for Contractor Response

When temperatures plunge below zero or a summer heatwave hits triple digits, every HVAC contractor knows what's coming: a flood of emergency calls. The problem? You're probably already on a job.

### The Emergency Dilemma

Picture this: You're replacing a furnace across town. Your phone rings—someone else has no heat (or AC). You can't answer. They call your competitor.

This scenario plays out thousands of times every season across the country. The contractors who solve this problem are the ones who grow their businesses.

### Solution 1: Hire More Staff

Some contractors try to solve this by hiring dispatchers or office staff. The math:
- Dispatcher salary: $3,500-4,500/month
- Benefits and overhead: Additional 20-30%
- Training time: 2-4 weeks before they're effective

For many small contractors, this just isn't feasible.

### Solution 2: Partner Contractors

Some try to partner with other contractors to share overflow. Problems:
- They're busy during the same peak times you are
- Quality control issues
- Revenue sharing cuts into margins

### Solution 3: AI Automation

Here's how AI handles winter emergencies:

1. **Instant Response:** Customer calls, AI answers immediately
2. **Emergency Triage:** AI asks qualifying questions (Is there heat? Gas smell? How cold is it inside?)
3. **Priority Flagging:** True emergencies get flagged for immediate callback
4. **Appointment Booking:** Non-emergencies get scheduled into the next available slot
5. **You Stay Informed:** Instant notifications so you can prioritize callbacks

### Real Results from Last Winter

One Anchorage HVAC contractor captured **12 emergency calls** in January that would have otherwise gone to voicemail. Average value per emergency call: $1,200.

**That's $14,400 in revenue that would have been lost.**

### Setting Up for Winter Success

The key is getting your system in place BEFORE the cold hits. Here's a timeline:

- **October:** Set up AI system and integrate with calendar
- **November:** Test and refine emergency response scripts
- **December:** Full deployment before the serious cold arrives

Don't wait until you're already overwhelmed. The contractors who prepare early are the ones who capture the most winter business.
    `,
    cta: true
  },
  'appointment-booking-automation': {
    content: `
## Stop Playing Phone Tag: The Complete Guide to Automated Booking

How much time do you spend every week on back-and-forth scheduling? For most contractors, it's hours of phone tag, text messages, and calendar juggling.

There's a better way.

### The True Cost of Manual Scheduling

Let's do the math:
- Average time per booking: 10-15 minutes (including callbacks)
- Bookings per week: 15-25
- Weekly time spent: 2.5-6 hours
- Your hourly rate: $75-150

**You're spending $200-900 per week on scheduling alone.**

### How Automated Booking Works

Modern booking systems integrate with your existing calendar and handle the entire process:

1. **Customer contacts you** (call, text, or web)
2. **AI engages in conversation** to understand their needs
3. **System checks your availability** in real-time
4. **Appointment is booked** into the next available slot
5. **Confirmations sent** to both parties

No phone tag. No double-bookings. No missed appointments.

### Key Features to Look For

When choosing an automated booking system, make sure it includes:

**Calendar Integration**
- Google Calendar
- Outlook
- Apple Calendar
- Industry-specific software (ServiceTitan, Housecall Pro, etc.)

**Smart Scheduling**
- Travel time buffers between appointments
- Service area restrictions
- Job type duration estimates

**Customer Communication**
- Appointment confirmations
- Reminder notifications
- Easy rescheduling options

**Business Rules**
- Minimum notice requirements
- Maximum bookings per day
- Emergency slot reservations

### Implementation Tips

1. **Start with your busiest service type.** Get that automated first, then expand.

2. **Build in buffer time.** Jobs often run long. Account for this in your scheduling.

3. **Keep emergency slots.** Reserve some capacity for urgent calls.

4. **Test thoroughly.** Book several test appointments before going live.

5. **Train your team.** Everyone should know how the system works.

### ROI Calculation

If automated booking saves you 4 hours per week:
- 4 hours × $100/hour = $400/week saved
- $400 × 52 weeks = **$20,800 per year**

Plus, you'll capture more leads because customers can book instantly instead of waiting for a callback.

The math is clear: automated booking pays for itself quickly.
    `,
    cta: true
  },
  'customer-response-time': {
    content: `
## Response Time: The Secret Weapon of Successful Contractors

Here's a statistic that should change how you think about your business:

**The first contractor to respond wins the job 78% of the time.**

Not the cheapest. Not the most experienced. The fastest.

### Why Speed Wins

When a customer has a problem—especially an emergency—they're in pain-seeking mode. They want relief NOW. The first contractor to offer that relief gets the job.

Consider this typical customer journey:

1. Problem occurs (furnace stops, pipe leaks, etc.)
2. Customer searches Google for contractors
3. Customer calls 3-4 businesses
4. **First to answer and offer a solution wins**

Steps 1-3 happen fast. Step 4 is where you win or lose.

### The Data Behind Response Time

Research from the Harvard Business Review found:

- Companies that respond within 5 minutes are **100x more likely** to qualify a lead
- After 30 minutes, the odds of qualifying drop by **21x**
- Most businesses take **47 hours** to respond (!)

### Your Current Response Time

Be honest with yourself. When someone calls your business:

- During work hours: How long until they reach a human?
- After hours: Do they get voicemail?
- On weekends: Who's answering?

If your answer to any of these isn't "instantly," you're losing business.

### Improving Your Response Time

**Option 1: Staff Up**
Hire dedicated phone staff. Expensive, but effective during business hours.

**Option 2: Answering Service**
Outsource to a call center. Decent for basic message-taking.

**Option 3: AI Automation**
Instant response, 24/7, with intelligent conversation and booking.

### The Compound Effect

Here's where it gets interesting. Faster response doesn't just win you the immediate job—it creates a compound effect:

1. **More jobs = More reviews**
2. **More reviews = Better Google ranking**
3. **Better ranking = More leads**
4. **More leads + fast response = Even more jobs**

The contractors who master response time create a virtuous cycle that competitors can't match.

### Action Steps

1. **Measure your current response time.** Have a friend call after hours.
2. **Set a target.** Under 60 seconds should be your goal.
3. **Implement a solution.** Whether it's staff, service, or AI.
4. **Monitor and improve.** Track your metrics monthly.

Response time is the easiest competitive advantage to create. Start today.
    `,
    cta: true
  },
  'roi-ai-automation': {
    content: `
## Calculating ROI: Is AI Lead Response Worth It?

Before investing in any business tool, you need to understand the return. Let's break down the real ROI of AI lead response automation.

### The Investment

**One-Time Setup:** $1,997
- System configuration
- Calendar integration
- Custom response training
- Industry-specific prompts

**Monthly Maintenance:** $397
- 24/7 operation
- Unlimited conversations
- Regular updates
- Support

**Year One Total:** $1,997 + ($397 × 12) = **$6,761**

### The Returns

Now let's look at what you get back.

#### 1. Captured Leads

Without AI, most contractors miss 30-50% of after-hours calls. Let's be conservative and say AI captures 10 additional leads per month.

- Additional leads/month: 10
- Average job value: $800
- Close rate on AI-captured leads: 40%
- **Monthly revenue gain: $3,200**

#### 2. Time Savings

No more phone tag, voicemail checking, or manual booking.

- Time saved/week: 4 hours
- Your hourly value: $100
- **Monthly time savings: $1,600**

#### 3. Customer Satisfaction

Faster response = happier customers = more referrals

- Increased referrals: 2/month (conservative)
- Referral job value: $1,000
- **Monthly referral revenue: $2,000**

### Total Monthly ROI

| Category | Monthly Value |
|----------|---------------|
| Captured Leads | $3,200 |
| Time Savings | $1,600 |
| Referrals | $2,000 |
| **Total** | **$6,800** |

### Payback Period

- Year One Cost: $6,761
- Month One Revenue: $6,800
- **Payback Period: Less than 1 month**

After the payback period, you're generating **$6,403 in monthly profit** ($6,800 - $397 maintenance).

### Conservative vs Optimistic Scenarios

**Conservative (Numbers above)**
- 10 leads/month, 40% close rate
- Payback: 1 month
- Year One Profit: $74,839

**Optimistic**
- 20 leads/month, 50% close rate
- Payback: Less than 1 month
- Year One Profit: $151,239

**Pessimistic**
- 5 leads/month, 30% close rate
- Payback: 4 months
- Year One Profit: $32,439

Even in the pessimistic scenario, the ROI is strongly positive.

### Hidden Benefits Not Included

These numbers don't account for:
- Reduced stress from not missing calls
- Better work-life balance
- Competitive advantage over slower competitors
- Improved Google reviews from happy customers

### The Bottom Line

For contractors doing at least $10,000/month in revenue, AI automation is essentially a no-brainer investment. The math works in virtually every scenario.

The only question is: how much business are you willing to lose while you decide?
    `,
    cta: true
  }
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  const content = blogContent[slug];

  if (!post || !content) {
    return <Navigate to="/blog" replace />;
  }

  const shareUrl = `https://alaskaaiservices.com/blog/${slug}`;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/blog" className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-white" />
                </div>
                <span className="font-bold text-lg hidden sm:block">Alaska AI Services</span>
              </div>
            </div>
            <Link 
              to="/blog"
              className="text-sm text-sky-400 hover:text-sky-300 transition-colors"
            >
              All Posts
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Article Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4 text-sm text-slate-400">
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {post.date}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {post.readTime}
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
            {post.title}
          </h1>
          <p className="text-xl text-slate-400">
            {post.excerpt}
          </p>
        </motion.div>

        {/* Share Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-4 mb-12 pb-8 border-b border-slate-800"
        >
          <span className="text-sm text-slate-500">Share:</span>
          <a 
            href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${encodeURIComponent(post.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Twitter className="w-5 h-5 text-slate-400 hover:text-sky-400" />
          </a>
          <a 
            href={`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}&title=${encodeURIComponent(post.title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Linkedin className="w-5 h-5 text-slate-400 hover:text-sky-400" />
          </a>
          <a 
            href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
          >
            <Facebook className="w-5 h-5 text-slate-400 hover:text-sky-400" />
          </a>
        </motion.div>

        {/* Article Content */}
        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="prose prose-invert prose-lg max-w-none
            prose-headings:font-bold prose-headings:text-white
            prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
            prose-p:text-slate-300 prose-p:leading-relaxed
            prose-strong:text-white prose-strong:font-semibold
            prose-ul:text-slate-300 prose-ol:text-slate-300
            prose-li:my-2
            prose-table:border-slate-700
            prose-th:bg-slate-800 prose-th:text-white prose-th:px-4 prose-th:py-2
            prose-td:border-slate-700 prose-td:px-4 prose-td:py-2
          "
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatMarkdown(content.content)) }}
        />

        {/* CTA Section */}
        {content.cta && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-16 bg-gradient-to-br from-sky-500/10 to-blue-600/10 border border-sky-500/20 rounded-2xl p-8 sm:p-12 text-center"
          >
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">
              Ready to Capture Every Lead?
            </h2>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto">
              See how Alaska AI Services responds to leads in under 60 seconds and books appointments automatically.
            </p>
            <a 
              href="https://calendly.com/jondcurtis"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white font-semibold px-8 py-4 rounded-lg transition-all"
            >
              Book a Free 10-Minute Demo
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        )}

        {/* Related Posts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <h3 className="text-xl font-bold mb-6">More Articles</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {blogPosts.filter(p => p.slug !== slug).slice(0, 2).map(relatedPost => (
              <Link key={relatedPost.slug} to={`/blog/${relatedPost.slug}`}>
                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition-all">
                  <h4 className="font-semibold mb-2 hover:text-sky-400 transition-colors line-clamp-2">
                    {relatedPost.title}
                  </h4>
                  <p className="text-sm text-slate-400 line-clamp-2">
                    {relatedPost.excerpt}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-slate-500 text-sm">
            © 2026 Alaska AI Services. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

// Simple markdown to HTML converter
function formatMarkdown(markdown) {
  return markdown
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^\*\*(.+?)\*\*/gm, '<strong>$1</strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^\- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>\n?)+/g, '<ul>$&</ul>')
    .replace(/^\d+\. (.+)$/gm, '<li>$1</li>')
    .replace(/\n\n/g, '</p><p>')
    .replace(/^\|(.+)\|$/gm, (match) => {
      const cells = match.split('|').filter(c => c.trim());
      const isHeader = cells.some(c => c.includes('---'));
      if (isHeader) return '';
      const tag = match.includes('---') ? 'th' : 'td';
      return `<tr>${cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('')}</tr>`;
    })
    .replace(/(<tr>.*<\/tr>\n?)+/g, '<table class="w-full border-collapse">$&</table>')
    .replace(/^(?!<[a-z])/gm, '<p>')
    .replace(/(?<![>])$/gm, '</p>')
    .replace(/<p><\/p>/g, '')
    .replace(/<p>(<[hultro])/g, '$1')
    .replace(/(<\/[hultro][^>]*>)<\/p>/g, '$1');
}
